import {
  useState,
  useEffect,
  ComponentPropsWithoutRef,
  PropsWithChildren,
} from "react";
import { Button } from "_/components/button";
import { Icon } from "_/components/icon";
import * as S from "./styled";

type DrawerOwnProps = {
  open: boolean;
  onClose?: () => void;
  width?: number | string;
  desktopBreakpoint?: number;
  side?: "left" | "right";
  zIndex?: number;
  lockBodyScroll?: boolean;
  useAppOffset?: boolean;
  desktopMode?: "inline" | "overlay";
  background?: string;
};

/** Only the native bits we want to expose from <nav> */
type DrawerNativeProps = Pick<
  ComponentPropsWithoutRef<"nav">,
  "className" | "style" | "role"
>;

/** Final public props */
export type DrawerProps = PropsWithChildren<DrawerOwnProps & DrawerNativeProps>;

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

function useBodyScrollLock(lock: boolean) {
  useEffect(() => {
    if (!lock) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lock]);
}

export function Drawer({
  open,
  onClose,
  width = "min(85vw, 300px)",
  desktopBreakpoint = 1025,
  side = "left",
  zIndex = 1,
  lockBodyScroll = true,
  className,
  style,
  role = "navigation",
  useAppOffset = true,
  desktopMode = "inline",
  background = "transparent",
  children,
}: DrawerProps) {
  const isDesktop = useMediaQuery(`(min-width: ${desktopBreakpoint}px)`);
  const desktopOverlay = desktopMode === "overlay";
  const isOverlayNow = !isDesktop || desktopOverlay;

  // Lock scroll whenever the overlay is shown
  useBodyScrollLock(lockBodyScroll && open && isOverlayNow);

  // Close on Escape only when an overlay is active (desktop)
  useEffect(() => {
    if (!onClose || !open || !isOverlayNow) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, open, isOverlayNow]);

  const widthCss = typeof width === "number" ? `${width}px` : width;

  return (
    <>
      <S.DrawerContainer
        className={className}
        style={style}
        role={role}
        aria-hidden={!(isDesktop || open)}
        $open={open}
        $bp={desktopBreakpoint}
        $side={side}
        $width={widthCss}
        $zIndex={zIndex}
        $useAppOffset={useAppOffset}
        $desktopOverlay={desktopOverlay}
        $background={background}
      >
        {/* Show the close button only when overlay is active */}
        {isOverlayNow && (
          <Button
            iconOnly
            kind="secondary"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon variant="Close" />
          </Button>
        )}
        {children}
      </S.DrawerContainer>

      {/* Backdrop also on desktop when overlay mode is requested */}
      <S.Backdrop
        aria-hidden
        $show={open && isOverlayNow}
        $bp={desktopBreakpoint}
        $desktopOverlay={desktopOverlay}
        $useAppOffset={useAppOffset}
        onClick={onClose}
      />
    </>
  );
}

export default Drawer;
